/*
    NEW:
    G8P410CM
    U55WNQWC

    LEGACY:
    Y6VLZ3J2
    CRZXXB47
*/

// main.ts
import './styles/style.css';
import { getAppId } from "./appId"; // no .js in TS

//import type { BoardData, SkinContext } from "./types";
//import { applySkin } from "./skinLoader";
import { applySnapshot, resetBoard } from "./skinLoader";
import { toAgenda, toSettings, toHeader, toEpochSeconds } from "./adapters";
import { getParam, setParam, removeParam, coercers, ensureDefaults } from "./inputParams";
import type { Session } from "./types";
import { DISPLAY_LOCALE, MAX_MESSAGES, REFRESH_INTERVAL, API_KEY, PERMISSION_TYPE, DISPLAY_TZ } from './config';

import { parseLocalInTZ } from "@utils/date";
 import { renderMessage } from "@ui/messages";
//import { renderMessage } from "./ui/messages";

//import { selectSessionsForBoard, type SelectionOptions } from "./utils/sessions";

const appId: string = getAppId;

// --- API endpoints/constants ---
const apiUrl = "https://api.mediamotor.net/";
const signinUrl = `${apiUrl}auth/internal`;
const agendaUrl = `${apiUrl}agenda`;       // (kept for parity; not used directly)
const boardsUrl = `${apiUrl}boards`;
const signoutUrl = `${apiUrl}auth/signout`;
const refreshUrl = `${apiUrl}auth/refresh`;

let abortCtrl: AbortController | undefined;

// const MAX_MESSAGES = 35;
// const REFRESH_INTERVAL = 600 * 1000;
// const API_KEY = "baad1df678659b42bdaac072240733ab33c6a773c59219efdeac8022539d6493";
// const PERMISSION_TYPE = "BOARD";

// Check / parse any URLparams
//const url = new URL(location.href);
const queryParams = new URLSearchParams(location.search);
const forceLogin = /^(1|true|yes)$/i.test(queryParams.get("autologin") ?? "");
// console.log('queryParams', queryParams)
// console.log('forceLogin', forceLogin); // false if not there

export interface ApplySnapshotOptions {
  /** For dev/testing: pretend "now" is this date */
  baseDate?: Date;
  /** If we selected "today", hide sessions that have already finished */
  showUnfinishedOnlyToday?: boolean;
  /** Time zone to evaluate day boundaries. Defaults to browser TZ; set to "Europe/Stockholm" if you prefer fixed. */
  timeZone?: string;
}






// --- Minimal types inferred from usage ---
type IsSignedInDetail = { 
  message: string;
  value: boolean;
};

type SessionData = { 
  sessioninc: number | string;
  sessiontitle: string;
};

type Appearance = {
  sessioninc: number | string;
  appearancetitle: string;
  // appearanceinc?: number | string; // (commented code)
};

type SettingsFile = {
  type: string; // e.g., 'background'
  signedUrl: string;
  timeStamp: number;
};

type Settings = { files?: SettingsFile[] };
type AgendaData = {
  sessions: SessionData[];
  appearances: Appearance[];
  cache?: boolean;
};

type BoardsPayload = {
  agenda: AgendaData & { cache?: boolean };
  settings: Settings & { cache?: boolean };
  requestTime?: string;
  label?: string;
  agendaTitle?: string;
  timestamp?: string;
};

type ApiResponse<T> = { data: T; error?: string };

type FetchOk<T> = { status: "success"; data: T };
type FetchErr = { status: "error"; message: string };
type FetchResult<T> = FetchOk<T> | FetchErr;



// --- DOM ---
const _elemSignin  = document.getElementById('signin');
const _container   = document.getElementById('container');
const _messages    = document.getElementById('messages');
const _sContainer  = document.getElementById('sContainer');
const _sMessage    = document.getElementById('sMessage');

assertExists(_elemSignin, 'signin');
assertExists(_container, 'container');
assertExists(_messages, 'messages');
assertExists(_sContainer, 'sContainer');
assertExists(_sMessage, 'sMessage');

// Non-null from here, so other functions see the right type
const elemSignin: HTMLElement = _elemSignin;
const container: HTMLElement  = _container;
const messages: HTMLElement   = _messages;
const sContainer: HTMLElement = _sContainer;
const sMessage: HTMLElement   = _sMessage;

function assertExists<T>(v: T | null | undefined, name = 'value'): asserts v is T {
  if (v == null) throw new Error(`Missing required DOM element: ${name}`);
}

let refreshTimer: number | undefined;

sMessage.textContent = "Please wait...";

// --- Event: isSignedIn ---
const handleIsSignedIn = (event: CustomEvent<IsSignedInDetail>) => {
  
  // console.log('handleIsSignedIn - init', event.detail)

  if (event.detail.value) {
    // User is signed in!
    refreshTimer = window.setTimeout(async () => {
      await getAgenda();

      document.dispatchEvent(
        new CustomEvent<IsSignedInDetail>("isSignedIn", {
          detail: {
            message: "Restarting timer",
            value: event.detail.value,
          },
        })
      );
    }, REFRESH_INTERVAL);
  } else {
    // User is NOT signed in!
    if (refreshTimer !== undefined) window.clearTimeout(refreshTimer);

    abortCtrl?.abort();   // ← cancels the in-flight fetch (if any)
    abortCtrl = undefined;

    console.log(' User is now signed out ');

    resetBoard(container);







  }
};
document.addEventListener("isSignedIn", handleIsSignedIn as EventListener);

// --- Functions ---
async function getAgenda(): Promise<{ messages: string } | null> {
  try {
    const agendaResponse = await fetchWithAuth<ApiResponse<BoardsPayload>>(boardsUrl, {});

    console.log("getAgenda - agendaResponse", agendaResponse);

    if (agendaResponse.status === "success") {
      const payload = agendaResponse.data.data;

      let message = payload.agenda.cache ? "New Agenda data from cache" : "New Agenda from source";
      message += payload.settings.cache
        ? "<br>New Settings data from cache"
        : "<br>New Settings from source";

      renderMessage({ message });

      // renderOutput({
      //   data: payload.agenda,
      //   // filter: payload.list, // (left as in original comment)
      // });

      
      await applySnapshot(
        {
          settings: toSettings(payload.settings),
          agenda: toAgenda(payload.agenda, { sort: true }),
          ...toHeader(payload), // agendaTitle, label, (optional) timestamp
          // version:   payload.version,     // if you have one
          requestTime: toEpochSeconds(payload.requestTime),
          requestTimeISO: payload.requestTime,
        },
        document.getElementById("container")!,    // only renders inside #container
        "default",                                // fallback skin name mm-skin-default
        { 
          showUnfinishedOnlyToday: true,          // today→unfinished; else tomorrow; else “No more…”
          //baseDate: parseLocalInTZ("2022-05-02T10:00:00", DISPLAY_TZ),
        }                               
      );

      renderSettings({
        label: payload.label ?? "BOARD",
        agendaTitle: payload.agendaTitle ?? "na",
        settings: payload.settings,
      });
    }

    return { messages: "Agenda retrieved" };
  } catch (error) {
    console.log("getAgenda error", error);
    return null;
  }
}

async function fetchWithAuth<T = unknown>(
  url: string,
  _options: RequestInit = {}
): Promise<FetchResult<T>> {
  let accessToken = sessionStorage.getItem("accessToken"); // null if absent
  let sessionId = sessionStorage.getItem("sessionId");
  let refreshRetries = 0;

  const fetchRequest = async (): Promise<FetchResult<T>> => {

    abortCtrl = new AbortController();
    const { signal } = abortCtrl;

    try {
      if (!accessToken || !sessionId) {
        console.error(
          "fetchWithAuth - Missing accessToken/sessionId (maybe refresh token expired after sleep)."
        );
        const refreshed = await refreshAccessToken();
        if (!refreshed) {
          const signedOut = await trySignOut();
          if (signedOut.status === "success") {
            renderSignInState(false, false);
          }
          throw new Error("Unable to refreshToken");
        }
        accessToken = sessionStorage.getItem("accessToken");
        sessionId = sessionStorage.getItem("sessionId");
      }

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
          "X-App-ID": appId,
          "X-Session-Id": sessionId ?? "",
        },
        credentials: "include",
        signal,
      });

      const data: unknown = await response.json();

      if (!response.ok) {
        const errObj = (data as Record<string, unknown>) ?? {};
        console.error("Fetch response error", errObj);

        renderMessage({
          type: "ERROR",
          message: (errObj.error as string) ?? "Unknown error",
        });

        if ((errObj.error as string) === "TokenExpiredError" && refreshRetries < 1) {
          refreshRetries++;
          const refreshed = await refreshAccessToken();
          if (!refreshed) {
            throw new Error(`Unable to refreshToken. refreshRetries = ${refreshRetries}`);
          }
          accessToken = sessionStorage.getItem("accessToken");
          sessionId = sessionStorage.getItem("sessionId");
          return await fetchRequest();
        }
        throw new Error((errObj.error as string) ?? "Request failed");
      }

      // Success
      return { status: "success", data: data as T };
    }
    catch (error) {
      const err = error as Error;
      console.error("fetchWithAuth Error (unhandled):", err);
      console.log("error.name", err.name);
      console.log("error.message", err.message);

      renderMessage({ type: "ERROR", message: "fetch Error" });

      return { status: "error", message: err.message };
    }
    finally {
      abortCtrl = undefined;
    }

  };

  return await fetchRequest();
}

async function refreshAccessToken(): Promise<{ accessToken: string; sessionId: string } | null> {
  try {
    const refreshResponse = await fetch(refreshUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include", // send cookies
    });

    if (!refreshResponse.ok) {
      throw new Error("Failed to refresh token");
    }

    const data = (await refreshResponse.json()) as ApiResponse<{ accessToken: string; sessionId: string }>;
    sessionStorage.setItem("accessToken", data.data.accessToken);
    sessionStorage.setItem("sessionId", data.data.sessionId);

    renderMessage({ type: "SUCCESS", message: "AccessToken refreshed" });

    return {
      accessToken: (data.data as any).accessToken,
      sessionId: (data.data as any).sessionId,
    };
  } catch (error) {
    const err = error as Error;
    console.error("Unable to refresh token:", err);
    renderMessage({ message: "refreshAccessToken error" });
    return null;
  }
}

function renderSignInState(signedIn: boolean, allowForceLogin: boolean = true ): void {
  // Read a control as a STRING for the coercer
  function readAsString(id: string): string {
    const el = document.getElementById(id) as HTMLInputElement | null;
    if (!el) return "";                         // no element -> treat as empty
    if (el.type === "checkbox") return el.checked ? "true" : "false";
    return el.value.trim();                     // text/number -> raw string
  }

  if (signedIn) {
    const elemSignedIn = document.createElement("div");
    const btnSignOut = document.createElement("button");
    const btnDefaultLabel2 = "Sign out";

    btnSignOut.textContent = btnDefaultLabel2;
    btnSignOut.addEventListener("click", async (event) => {
      event.preventDefault();

      const message = "Signing out...";
      if (btnSignOut.textContent === message) return; // already in progress

      btnSignOut.textContent = message; // "Signing out..."

      // stop refresh timer
      document.dispatchEvent(
        new CustomEvent<IsSignedInDetail>("isSignedIn", {
          detail: { 
            message: "Signing Out...",
            value: false
          },
        })
      );

      try {
        const signedOut = await trySignOut();
        if (signedOut.status === "success") renderSignInState(false, false);
      } catch (error) {
        console.error("Network Error:", error);
        renderMessage({ type: "ERROR", message: "Network error" });
      }
    });
    elemSignedIn.append(btnSignOut);
    elemSignin.replaceChildren(elemSignedIn);



    type InputKeys = keyof typeof coercers;

    // Map form control IDs -> InputKeys in your store
    const fieldMap: Record<string, InputKeys> = {
      //meetingId:      "meetingId",
      //tokenId:        "tokenId",
      num_items:      "num_items",
      render_time:    "render_time",   // checkbox
      bgColor:        "bgColor",
      color:          "color",
      marginTop:      "marginTop",
      marginRight:    "marginRight",
      marginBottom:   "marginBottom",
      marginLeft:     "marginLeft",
      marginGuides:   "marginGuides",  // checkbox
    };




    // Parameters
    const elParametesContainer = document.createElement('div');
    elParametesContainer.className = 'paramsContainer';
    
    // Get inputParams from localStorage
    const paramColor = getParam('color') || '';
    const paramBgColor = getParam('bgColor') || '';
    const paramNumItems = getParam('num_items') || '';
    const paramMTop = getParam('marginTop') || '';
    const paramMRight = getParam('marginRight') || '';
    const paramMBottom = getParam('marginBottom') || '';
    const paramMLeft = getParam('marginLeft') || '';
    const paramRenderMargin = ["true","1"].includes(String(getParam("marginGuides") || ""));
    const paramRenderTime = ["true","1"].includes(String(getParam("render_time") || ""));

    const regExPattern = String.raw`^(?:#(?:[0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})|rgb\(\s*(?:25[0-5]|2[0-4]\d|1?\d?\d)\s*,\s*(?:25[0-5]|2[0-4]\d|1?\d?\d)\s*,\s*(?:25[0-5]|2[0-4]\d|1?\d?\d)\s*\)|rgba\(\s*(?:25[0-5]|2[0-4]\d|1?\d?\d)\s*,\s*(?:25[0-5]|2[0-4]\d|1?\d?\d)\s*,\s*(?:25[0-5]|2[0-4]\d|1?\d?\d)\s*,\s*(?:0(?:\.\d+)?|1(?:\.0+)?|0?\.\d+)\s*\))$`;

    const btnDefaultLabel = 'Update';

    const form = document.createElement('form');
    form.setAttribute("id", "paramsForm");
    form.setAttribute("method", "POST");
    form.innerHTML = `
      <label for="color">Color:</label>
      <input type="text" id="color" name="color" placeholder="#0099ff or rgb(0,0,0) or rgba(0,0,0,0.5)" value="${paramColor}" pattern="${regExPattern}">
      <br>
      <label for="bgColor">Background color:</label>
      <input type="text" id="bgColor" name="bgColor" placeholder="#0099ff or rgb(0,0,0) or rgba(0,0,0,0.5)" value="${paramBgColor}" pattern="${regExPattern}">
      <br>
      <label for="num_items">Max items:</label>
      <input type="number" min="0" step="1" id="num_items" name="num_items" placeholder="Max items" value="${paramNumItems}">
      <br>
      <label for="marginTop">Top margin:</label>
      <input type="number" min="0" step="1" id="marginTop" name="marginTop" placeholder="Top margin" value="${paramMTop}">
      <br>
      <label for="marginRight">Right margin:</label>
      <input type="number" min="0" step="1" id="marginRight" name="marginRight" placeholder="Right margin" value="${paramMRight}">
      <br>
      <label for="marginBottom">Bottom margin:</label>
      <input type="number" min="0" step="1" id="marginBottom" name="marginBottom" placeholder="Bottom margin" value="${paramMBottom}">
      <br>
      <label for="marginLeft">Left margin:</label>
      <input type="number" min="0" step="1" id="marginLeft" name="marginLeft" placeholder="Left margin" value="${paramMLeft}">
      <br>
      <label for="marginGuides">Show margins:</label>
      <input type="checkbox" id="marginGuides" name="marginGuides" ${paramRenderMargin ? "checked" : ""}>
      <br>
      <label for="render_time">Show time:</label>
      <input type="checkbox" id="render_time" name="render_time" ${paramRenderTime ? "checked" : ""}>
      <br>
      <button id="btnUpdate" type="submit">${btnDefaultLabel}</button>
    `;
    form.addEventListener("submit", async (event: SubmitEvent) => {
      event.preventDefault();
      const form = event.currentTarget as HTMLFormElement;
      if (!form.checkValidity()) {
        form.reportValidity();
        return;
      }

      console.log('Update click');

      const btnUpdate = document.getElementById("btnUpdate") as HTMLButtonElement | null;
      if (!btnUpdate) return;

      const message = "Please wait...";
      if (btnUpdate.textContent === message) return; // already in progress

      btnUpdate.textContent = message;

      // Iterate all mapped fields
      (Object.keys(fieldMap) as Array<keyof typeof fieldMap>).forEach((id) => {
        const key = fieldMap[id];
        const raw = readAsString(id);             // ALWAYS a string for coercer
        const val = coercers[key](raw);           // typed value | undefined
        
        // console.log(`${key} - ${key} : ${val}`);

        setParam(key, val);                       // store or remove
      });

      // Update Shadowhost on Container
      if (container) {
        
        const cssVal = (v: unknown, unitDefault = "%") => v == null ? undefined : typeof v === "number" ? `${v}${unitDefault}` : String(v);


        const mt = cssVal(getParam("marginTop"));
        const mr = cssVal(getParam("marginRight"));
        const mb = cssVal(getParam("marginBottom"));
        const ml = cssVal(getParam("marginLeft"));

        const guides = !!getParam("marginGuides"); // coerce to boolean

        const color   = getParam("color");
        const bgColor = getParam("bgColor");

        console.log('marginGuides', getParam("marginGuides"));

        // *** FIX get skinName here

        const skinName = "mm-skin-default";

        document.querySelectorAll<HTMLElement>(skinName).forEach(host => {

          // Margins
          if (mt !== undefined) host.style.setProperty("--wrap-mt", mt);
          if (mr !== undefined) host.style.setProperty("--wrap-mr", mr);
          if (mb !== undefined) host.style.setProperty("--wrap-mb", mb);
          if (ml !== undefined) host.style.setProperty("--wrap-ml", ml);

          // Guides
          // num items
          host?.classList.toggle("show-margins", !!getParam("marginGuides"));

          
          
          // Colors
          if (color)   host.style.setProperty("--wrap-color", color);
          if (bgColor) host.style.setProperty("--wrap-bgcolor", bgColor);

    
          

          // autologin ??? trac??





        })
      }

      // Reset button label
      btnUpdate.textContent = btnDefaultLabel;



      // const $ = (id: string) => document.getElementById(id) as HTMLInputElement | null;

      // setParam("color",   textOrUndef($("color")));   // string | undefined
      // setParam("bgcolor", textOrUndef($("bgcolor"))); // string | undefined
      // setParam("numitems", intOrUndef($("numitems"))); // number | undefined
      // setParam("mtop",     intOrUndef($("mtop")));
      // setParam("mright",   intOrUndef($("mright")));
      // setParam("mbottom",  intOrUndef($("mbottom")));
      // setParam("mleft",    intOrUndef($("mleft")));
      // setParam("mshow", boolVal($("mshow"))); // boolean
      // setParam("tshow", boolVal($("tshow"))); // boolean


      // const colorInput = document.getElementById("color") as HTMLInputElement | null;
      // const bgcolorInput = document.getElementById("bgcolor") as HTMLInputElement | null;
      // const numitemsInput = document.getElementById("numitems") as HTMLInputElement | null;
      // const mtopInput = document.getElementById("mtop") as HTMLInputElement | null;
      // const mrightInput = document.getElementById("mright") as HTMLInputElement | null;
      // const mbottomInput = document.getElementById("mbottom") as HTMLInputElement | null;
      // const mleftInput = document.getElementById("mleft") as HTMLInputElement | null;
      // const mshowInput = document.getElementById("mshow") as HTMLInputElement | null;
      // const tshowInput = document.getElementById("tshow") as HTMLInputElement | null;

      // const color      = colorInput?.value ?? "";
      // const bgcolor    = bgcolorInput?.value ?? "";
      // const numitems   = numitemsInput?.value ?? "";
      // const mtop       = mtopInput?.value ?? "";
      // const mright     = mrightInput?.value ?? "";
      // const mbottom    = mbottomInput?.value ?? "";
      // const mleft      = mleftInput?.value ?? "";
      // const mshow      = !!mshowInput?.checked;  // boolean
      // const tshow      = !!tshowInput?.checked;  // boolean






    })

    elParametesContainer.append(form);

    elemSignin.append(elParametesContainer);

    sMessage.textContent = ``;
    renderMessage({ message: "Logged in!" });

    // Splash
    // document.body.querySelectorAll('#splash').forEach((item) => {
    //   item.remove();
    // });
    document.querySelector("#splash")?.remove();
  }
  else {
    // Stored if succeful previous attempt Y6VLZ3J2 CRZXXB47
    //const inputMeetingId = sessionStorage.getItem("inputMeetingId") || "";
    //const inputTokenId = sessionStorage.getItem("inputTokenId") || "";
    const inputMeetingId = getParam("meetingId") || "";
    const inputTokenId = getParam("tokenId") || "";
    const btnDefaultLabel = 'Sign In';

    const form = document.createElement("form");
    form.setAttribute("id", "loginForm");
    form.setAttribute("method", "POST");
    form.innerHTML = `
      <label for="meetingId">Meeting ID:</label>
      <input type="text" id="meetingId" name="meetingId" placeholder="Enter Meeting ID" value="${inputMeetingId}" required>
      <br>
      <label for="tokenId">Token ID:</label>
      <input type="password" id="tokenId" name="tokenId" placeholder="Enter Token ID" value="${inputTokenId}" required>
      <br>
      <button id="btnSignin" type="submit">${btnDefaultLabel}</button>
    `;

    form.addEventListener("submit", async (event: SubmitEvent) => {
      event.preventDefault();

      const btnSignin = document.getElementById("btnSignin") as HTMLButtonElement | null;
      if (!btnSignin) return;

      const message = "Signing in...";
      if (btnSignin.textContent === message) return; // already in progress

      btnSignin.textContent = message;

      const meetingInput = document.getElementById("meetingId") as HTMLInputElement | null;
      const tokenInput = document.getElementById("tokenId") as HTMLInputElement | null;
      const meetingId = meetingInput?.value ?? "";
      const tokenId = tokenInput?.value ?? "";

      const requestData = {
        meetingId,
        tokenId,
        apiKey: API_KEY,
        permissionType: PERMISSION_TYPE,
      };

      try {
        const response = await fetch(`${signinUrl}?timestamp=${Date.now()}`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-App-ID": appId,
          },
          credentials: "include",
          body: JSON.stringify(requestData),
        });

        const responseData = (await response.json()) as ApiResponse<{ accessToken: string; sessionId: string }>;

        if (response.ok) {
          sessionStorage.setItem("accessToken", responseData.data.accessToken);
          sessionStorage.setItem("sessionId", responseData.data.sessionId);
          sessionStorage.setItem("meetingId", meetingId);
          sessionStorage.setItem("tokenId", tokenId);
          
          // Saving user input
          // WARN: Only triggers on fresh login, not autologin!
          // setParam("meetingId", meetingId);
          // setParam("tokenId", tokenId);
          // setParam("marginTop", 50);
          // setParam("marginRight", 25);
          // setParam("marginBottom", 5);
          // setParam("marginLeft", 10);
          // setParam("marginGuides", true);
          // setParam("color", 'rgba(20,90,200,0.5)');
          
          renderSignInState(true);

          await getAgenda();

          document.dispatchEvent(
            new CustomEvent<IsSignedInDetail>("isSignedIn", {
              detail: {
                message: "User signed in!",
                value: true
              },
            })
          );
        } else {
          btnSignin.textContent = btnDefaultLabel;
          renderMessage({ type: "ERROR", message: "Login failed" });
        }
      } catch (error) {
        console.error("Network Error:", error);

        if (error instanceof TypeError) {
          console.error("Network/Type error:", error.message);
        } else if (error instanceof Error) {
          console.error("Other error:", error.message);
        } else {
          console.error("Unknown error:", String(error));
        }

        btnSignin.textContent = btnDefaultLabel;
        renderMessage({ type: "ERROR", message: "Network error" });
      }
    });

    elemSignin.replaceChildren(form);

    sMessage.textContent = ``;
    renderMessage({ type: "ERROR", message: "Not logged in" });

    // Splash
    if(!document.querySelector("#splash")) {
      const elemSplash = document.createElement('div');
      elemSplash.id = 'splash';
      elemSplash.className ='splash';
      elemSplash.innerHTML =  `<h1>MEDIAMOTOR</h1>
      <h2>Pull down menu by moving mouse cursor up</h2>`;
      document.body.append(elemSplash);
    }

    // If autologin parameter in URL
    if(allowForceLogin && forceLogin) {
      
      //console.log('force attempt');
      
      const renderedForm = document.getElementById("loginForm") as HTMLFormElement | null;

      if(renderedForm) {
        if (typeof (form as any).requestSubmit === "function") form.requestSubmit();
        else {
          const btn = form.querySelector('button[type="submit"], input[type="submit"]') as
            | HTMLButtonElement
            | HTMLInputElement
            | null;

            if (btn) btn.click();
            else form.dispatchEvent(new Event("submit", { bubbles: true, cancelable: true }));
        }
      }
    }
    else {
      // console.log('no force attempt')
    }
  }

  renderSettings({});
}

async function trySignOut(): Promise<{ status: "success" | "error" }> {
  const accessToken = sessionStorage.getItem("accessToken");
  const sessionId = sessionStorage.getItem("sessionId");

  const response = await fetch(signoutUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
      "X-App-ID": appId,
    },
    credentials: "include",
    body: JSON.stringify({ sessionId }),
  });

  const responseData = (await response.json()) as { error?: string };

  if (response.ok) {
    document.dispatchEvent(
      new CustomEvent<IsSignedInDetail>("isSignedIn", {
        detail: {
          message: "User signed out!",
          value: false,
        },
      })
    );

    sessionStorage.removeItem("accessToken");
    sessionStorage.removeItem("sessionId");
    sessionStorage.removeItem("meetingId");
    sessionStorage.removeItem("tokenId");

    return { status: "success" };
  } else {
    console.error("Sign Out Failed:", `Error: ${responseData.error || "Login failed"}`);
    renderMessage({ type: "ERROR", message: "Login failed" });
    return { status: "error" };
  }
}

// function renderMessage(params: { message?: string; type?: "SUCCESS" | "WARN" | "ERROR" | "INFO" }) {
//   //const time = new Date().toISOString(); // Gives UTC time
//   const time = new Intl.DateTimeFormat(DISPLAY_LOCALE, {
//     timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone, // browser tz
//     dateStyle: "short",
//     timeStyle: "medium",
//   }).format(new Date()); // Ex. 2025-08-26 10:48:04 (Browser time, sv-SE)


//   const message = params.message ?? "empty";
//   const type = params.type ?? "INFO";

//   const elem = document.createElement("div");
//   elem.classList.add("messageBox");
//   elem.innerHTML = `
//     <div>${time}</div>
//     <div>${message}</div>
//   `;

//   switch (type) {
//     case "SUCCESS":
//       elem.setAttribute("style", "background-color:#e2ffe2;");
//       break;
//     case "WARN":
//       elem.setAttribute("style", "background-color:#fffbd9;");
//       break;
//     case "ERROR":
//       elem.setAttribute("style", "background-color:#ffc0c0;");
//       break;
//   }

//   messages!.prepend(elem);

//   while ((messages!.childElementCount) > MAX_MESSAGES) {
//     messages!.removeChild(messages!.lastChild as ChildNode);
//   }
// }

// function renderOutput(params: { data: AgendaData; filter?: (number | string)[] }) {
//   const root = document.createElement("div");

//   for (const s of params.data.sessions) {
//     if ("filter" in params && params.filter && !params.filter.includes(s.sessioninc)) {
//       continue;
//     }

//     const elemS = document.createElement("div");
//     elemS.classList.add("session");
//     elemS.innerHTML = `<span>${s.sessiontitle}</span>`;
//     root.append(elemS);

//     for (const a of params.data.appearances) {
//       if (s.sessioninc === a.sessioninc) {
//         const elemA = document.createElement("div");
//         elemA.classList.add("appearance");
//         elemA.innerHTML = `<span>${a.appearancetitle}</span>`;
//         root.append(elemA);
//       }
//     }
//   }

//   if (container!.innerHTML === root.outerHTML) {
//     renderMessage({ message: "Agenda has not changed..." });
//   } else {
//     container!.replaceChildren(root);
//   }
// }

// function renderSettings(params: {
//   label?: string;
//   agendaTitle?: string;
//   settings?: Settings;
// }) {
//   const meetingId = sessionStorage.getItem("meetingId") || "";
//   const tokenId = sessionStorage.getItem("tokenId") || "";
//   const label = params.label ?? "BOARD";
//   const agendaTitle = params.agendaTitle ?? "na";

//   sContainer!.innerHTML = `
//     <div><span>meetingId:</span>${meetingId}</div>
//     <div><span>tokenId:</span>${tokenId}</div>
//     <div><span>label:</span>${label}</div>
//     <div><span>Meeting:</span>${agendaTitle.slice(0, 10)}</div>
//     <div><span>MAX_MESSAGES:</span>${MAX_MESSAGES}</div>
//     <div><span>REFRESH_INTERVAL:</span>${REFRESH_INTERVAL / 1000}s</div>
//   `;

//   const elemBody = document.body;
//   const bgFile = params.settings?.files?.find((x) => x.type === "background") ?? null;

//   if (bgFile) {
//     const attributeName = "data-bgtime";
//     const timeStamp = parseInt(elemBody.getAttribute(attributeName) || "", 10) || null;

//     if (timeStamp !== bgFile.timeStamp) {
//       const img = new Image();
//       img.onload = function () {
//         elemBody.style.minHeight = `${img.naturalHeight}px`;
//         elemBody.style.backgroundImage = `url("${img.src}")`;
//         elemBody.style.backgroundSize = "auto";
//         elemBody.style.backgroundRepeat = "no-repeat";
//         elemBody.style.backgroundPosition = "top left";
//       };
//       img.src = bgFile.signedUrl;

//       elemBody.setAttribute(attributeName, String(bgFile.timeStamp));

//       renderMessage({ type: "SUCCESS", message: "New background file loaded." });
//     }
//   } else {
//     elemBody.style.minHeight = "";
//     elemBody.style.backgroundImage = "";
//     elemBody.style.backgroundSize = "";
//     elemBody.style.backgroundRepeat = "";
//     elemBody.style.backgroundPosition = "";

//     renderMessage({ type: "SUCCESS", message: "Background file removed." });
//   }
// }

function renderSettings(params: {
  label?: string;
  agendaTitle?: string;
  settings?: Settings;
}) {
  const meetingId = sessionStorage.getItem("meetingId") || "";
  const tokenId = sessionStorage.getItem("tokenId") || "";
  const label = params.label ?? "BOARD";
  const agendaTitle = params.agendaTitle ?? "na";

  sContainer!.innerHTML = `
    <div><span>meetingId:</span>${meetingId}</div>
    <div><span>tokenId:</span>${tokenId}</div>
    <div><span>label:</span>${label}</div>
    <div><span>Meeting:</span>${agendaTitle.slice(0, 10)}</div>
    <div><span>MAX_MESSAGES:</span>${MAX_MESSAGES}</div>
    <div><span>REFRESH_INTERVAL:</span>${REFRESH_INTERVAL / 1000}s</div>
  `;

  // No background handling here anymore.
  // setBackground(snapshot.settings) is called from applySnapshot after toSettings()
}

async function autoLogin(): Promise<void> {
  renderMessage({ type: "INFO", message: "Logging started..." });

  try {
    const response = await fetch(refreshUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
    });

    if (!response.ok) {
      renderMessage({ type: "WARN", message: "Auto-login failed" });
      renderSignInState(false);
      return;
    }

    const data = (await response.json()) as ApiResponse<{ accessToken: string; sessionId?: string }>;

    console.log("CAN WE GET NEW SESSIONID?? also need new refreshtoken here?", data);

    sessionStorage.setItem("accessToken", data.data.accessToken);
    // sessionStorage.setItem("sessionId", (data.data as any).sessionId); // intentional: old sessionId may persist

    renderMessage({ type: "INFO", message: "Auto-login success" });

    await getAgenda();

    document.dispatchEvent(
      new CustomEvent<IsSignedInDetail>("isSignedIn", {
        detail: { 
          message: "User signed in (auto)!",
          value: true
        },
      })
    );

    renderSignInState(true);
  }
  catch (error) {
    console.error("Error auto-logging in:", error);

    if (error instanceof TypeError) {
      console.error("Network/Type error:", error.message);
    } else if (error instanceof Error) {
      console.error("Other error:", error.message);
    } else {
      console.error("Unknown error:", String(error));
    }

    renderMessage({ type: "INFO", message: "Unable to auto-login" });
    renderSignInState(false);
  }
}

document.addEventListener("DOMContentLoaded", () => {

  // Handle menu show/hide
  const menu = document.querySelector(".menu") as HTMLElement | null;
  if (!menu) return;
  menu.style.display = "none";  // Start hidden
  let lastState = false;        // tracks if menu was visible

  document.addEventListener("mousemove", (e: MouseEvent) => {
    const show = e.clientY < window.innerHeight / 2;
    if (show !== lastState) {
      menu.style.display = show ? "flex" : "none";
      lastState = show;
    }
  });

  // Handle inputParams (Prefill form & Settings)
  ensureDefaults(); // If localStorage is empty, insert default values from .env

  //console.log('META', import.meta.env)


});

window.onload = autoLogin;
